// Update your Product.jsx to have a fixed height and scroll
import { Box, Tab, Tabs, TextField, InputAdornment } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import {
  getRaccourcisByOrganisation,
  getProductByBarCode,
} from "../../Services/MenuApi";
import IngredientPopup from "./Ingredients";
import QuickAddProductModal from "./QuickAddProductModal";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ClientSelector from "./ClientSelector";
import { getImageUrl } from "../../Helpers/Helpers";

const Product = ({
  categories,
  onAddToCart,
  organisationId,
  selectedClient,
  onClientSelect,
  onProductCreated,
  clientSearchCalcActive,
  clientSearchCalcBuffer,
  onActivateClientSearchCalc,
  onDeactivateClientSearchCalc,
  productSearchCalcActive,
  productSearchCalcBuffer,
  onActivateProductSearchCalc,
  onDeactivateProductSearchCalc,
}) => {
  const { t } = useTranslation();
  const [tabIndex, setTabIndex] = useState(0);
  const [raccourciGrids, setRaccourciGrids] = useState([]);
  const [loadingRaccourcis, setLoadingRaccourcis] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [loadingsearch, setloadingsearch] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Refs for barcode handling
  const timeoutRef = useRef(null);
  const lastKeyPressTime = useRef(0);
  const searchInputRef = useRef(null);

  // Original products list for filtering
  const [allProducts, setAllProducts] = useState([]);

  // Quick-add-product state (offered when a scanned barcode matches nothing)
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [pendingBarcode, setPendingBarcode] = useState("");

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setIsSearchMode(false);
    setSearchTerm("");
  };

  useEffect(() => {
    if (organisationId) {
      loadRaccourciGrids();
    }
  }, [organisationId]);

  // Build all products list when categories or raccourcis change
  useEffect(() => {
    const products = [];

    // Add products from categories
    if (categories) {
      categories.forEach((category) => {
        if (category.menus_list) {
          category.menus_list.forEach((menu) => {
            if (menu.dispo) {
              products.push({
                ...menu,
                categoryName: category.libelle?.fr,
                source: "category",
                categoryId: category._id,
              });
            }
          });
        }
      });
    }

    // Add products from raccourcis
    raccourciGrids.forEach((raccourci) => {
      if (raccourci.products) {
        raccourci.products.forEach((product) => {
          if (product.product_id) {
            products.push({
              ...product.product_id,
              raccourciName: raccourci.name,
              source: "raccourci",
            });
          }
        });
      }
    });

    setAllProducts(products);
  }, [categories, raccourciGrids]);

  const loadRaccourciGrids = async () => {
    try {
      setLoadingRaccourcis(true);
      const response = await getRaccourcisByOrganisation(organisationId);

      if (response.success && response.data) {
        // Group raccourcis by name
        const groupedRaccourcis = {};
        response.data?.data?.forEach((item) => {
          if (!groupedRaccourcis[item.raccourci_name]) {
            groupedRaccourcis[item.raccourci_name] = {
              name: item.raccourci_name,
              products: [],
              createdAt: item.createdAt,
              isRaccourci: true,
            };
          }
          if (item.product) {
            groupedRaccourcis[item.raccourci_name].products.push({
              ...item.product,
              libelle: item.product.titre?.fr || item.product.libelle,
            });
          }
        });

        // Convert to array and filter empty ones
        const raccourciArray = Object.values(groupedRaccourcis).filter(
          (r) => r.products.length > 0,
        );

        setRaccourciGrids(raccourciArray);
      }
    } catch (error) {
      console.error("Error loading raccourci grids:", error);
    } finally {
      setLoadingRaccourcis(false);
    }
  };

  const [ingredientPopupOpen, setIngredientPopupOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [currentProductIngredients, setCurrentProductIngredients] = useState(
    [],
  );
  const [selectedIngredients, setSelectedIngredients] = useState([]);

  const raccourciOffset = raccourciGrids.length;

  // Add a product found via barcode lookup to the cart (or open ingredient popup)
  const addScannedProduct = (product, { silent = false } = {}) => {
    if (product.ingredients && product.ingredients.length > 0) {
      setCurrentProduct(product);
      setCurrentProductIngredients(product.ingredients);
      setSelectedIngredients([]);
      setIngredientPopupOpen(true);
    } else {
      onAddToCart({
        id: product._id || product.id,
        name: product.titre?.fr || product.libelle,
        price: product.prix_ttc,
        ingredients: [],
        productInfo: product,
      });
      if (!silent) toast.success(t("Product added successfully"));
    }
  };

  // Look up a scanned barcode against the server, since the locally loaded
  // categories may not include every product (unavailable, not yet fetched, etc.)
  const searchByBarcode = async (barcode) => {
    setloadingsearch(true);
    try {
      const result = await getProductByBarCode({
        barcode,
        organisation: organisationId,
      });

      if (result?.data?.success && result.data.data) {
        addScannedProduct(result.data.data);
      } else {
        await offerToCreateProduct(barcode);
      }
    } catch (error) {
      console.error("Error searching by barcode:", error);
      toast.error(t("Error searching for product"));
    } finally {
      setloadingsearch(false);
      setSearchTerm("");
      setIsSearchMode(false);
    }
  };

  // No product matches this barcode — offer to create it on the spot
  const offerToCreateProduct = async (barcode) => {
    const result = await Swal.fire({
      title: t("Produit introuvable"),
      text: t("Aucun produit ne correspond à ce code-barres. Voulez-vous en créer un ?"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("Oui, créer"),
      cancelButtonText: t("Annuler"),
    });
    if (result.isConfirmed) {
      setPendingBarcode(barcode);
      setQuickAddOpen(true);
    }
  };

  const handleProductCreated = (product) => {
    setQuickAddOpen(false);
    // Skip the "added to cart" toast — the creation modal already confirmed success
    addScannedProduct(product, { silent: true });
    if (onProductCreated) onProductCreated();
  };

  // Filter products based on search term
  const filterProductsByName = (term) => {
    if (!term.trim()) {
      setFilteredProducts([]);
      setIsSearchMode(false);
      return;
    }

    const searchTerm = term.trim();
    const searchLower = searchTerm.toLowerCase();

    // First, check if it's a barcode (numeric)
    const isBarcode = /^\d+$/.test(searchTerm);

    if (isBarcode) {
      searchByBarcode(searchTerm);
      return;
    }

    // If not a barcode, do text search
    const filtered = allProducts.filter((product) => {
      const productName =
        product.titre?.fr?.toLowerCase() ||
        product.libelle?.toLowerCase() ||
        "";
      return productName.includes(searchLower);
    });

    setFilteredProducts(filtered);
    setIsSearchMode(true);
  };

  // Shared by native typing and the on-screen keyboard binding below —
  // both ultimately just feed a raw string through the same debounce/
  // barcode-vs-text logic.
  const processSearchValue = (rawValue) => {
    const cleanedValue = rawValue.replace(/\n/g, "").trim();
    setSearchTerm(rawValue); // Keep raw value for display

    const now = Date.now();
    const timeDiff = now - lastKeyPressTime.current;
    lastKeyPressTime.current = now;

    // Detect if it's a barcode scanner (fast input)
    const isScannerInput = timeDiff < 50 && rawValue.length > 3;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(
      () => {
        if (cleanedValue !== "") {
          // Treat as text search
          filterProductsByName(cleanedValue);
        } else {
          setIsSearchMode(false);
          setFilteredProducts([]);
        }
      },
      isScannerInput ? 100 : 500, // Shorter delay for scanner, longer for typing
    );
  };

  // Handle input change (for both barcode and text search)
  const handleInputChange = (e) => processSearchValue(e.target.value);

  // Mirror the shared on-screen keyboard's buffer into this input while
  // it's bound here, so typing on it drives the same search as native input.
  useEffect(() => {
    if (productSearchCalcActive) {
      processSearchValue(productSearchCalcBuffer || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchCalcBuffer, productSearchCalcActive]);

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm("");
    setIsSearchMode(false);
    setFilteredProducts([]);
    if (onDeactivateProductSearchCalc) onDeactivateProductSearchCalc();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleAddToCart = (menu) => {
    console.log("menu", menu);
    // If product has ingredients → open popup
    if (menu.ingredients && menu.ingredients.length > 0) {
      setCurrentProduct(menu);
      setCurrentProductIngredients(menu.ingredients);
      setSelectedIngredients([]);
      setIngredientPopupOpen(true);
      return;
    }

    // No ingredients → direct add
    onAddToCart({
      id: menu._id,
      name: menu.titre?.fr || menu.libelle,
      price: menu.prix_ttc,
      ingredients: [],
      productInfo: menu,
    });

    // If in search mode, keep search active but clear input
    if (isSearchMode) {
      setSearchTerm("");
      filterProductsByName("");
    }
  };

  const handleCheckboxChange = (event, ingredient) => {
    if (event.target.checked) {
      setSelectedIngredients((prev) => [...prev, ingredient]);
    } else {
      setSelectedIngredients((prev) =>
        prev.filter((i) => i._id !== ingredient._id),
      );
    }
  };

  // Get current products to display
  const getCurrentProducts = () => {
    if (isSearchMode) {
      return filteredProducts;
    }

    if (tabIndex < raccourciOffset) {
      return (
        raccourciGrids[tabIndex]?.products?.map((p) => p.product_id || p) || []
      );
    }

    return (
      categories[tabIndex - raccourciOffset]?.menus_list?.filter(
        (menu) => menu.dispo,
      ) || []
    );
  };

  return (
    <div
      className="content-wrapper pt-1 no-min-height"
      style={{
        margin: 0,
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 90px)", // Capping the height so it stops right before the bottom menu
      }}
    >
      <div
        className="container-full"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Prevents component overflowing outside the bounding height
        }}
      >
        <div style={{ padding: "0 5px 10px 5px", flexShrink: 0 }}>
          <ClientSelector
            organisation={organisationId}
            selectedClient={selectedClient}
            onClientSelect={onClientSelect}
            calcActive={clientSearchCalcActive}
            searchBuffer={clientSearchCalcBuffer}
            onActivateCalc={onActivateClientSearchCalc}
            onDeactivateCalc={onDeactivateClientSearchCalc}
          />
        </div>
        {/* Search Bar */}
        <div style={{ padding: "0 5px 10px 5px", flexShrink: 0 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("code a barre / nom de produit ...")}
            value={searchTerm}
            onChange={handleInputChange}
            onClick={() => {
              if (onActivateProductSearchCalc) onActivateProductSearchCalc(searchTerm);
            }}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i
                    className="fa fa-search"
                    style={{ fontSize: "14px", opacity: 0.7 }}
                  />
                </InputAdornment>
              ),
              endAdornment: loadingsearch ? (
                <InputAdornment position="end">
                  <Spinner animation="border" role="status" size="sm" />
                </InputAdornment>
              ) : searchTerm ? (
                <InputAdornment position="end">
                  <i
                    className="fa fa-times"
                    style={{
                      fontSize: "14px",
                      opacity: 0.7,
                      cursor: "pointer",
                    }}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: "20px",
                backgroundColor: productSearchCalcActive ? "#fff3ec" : undefined,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: productSearchCalcActive ? "#fd683e" : "#e0e0e0",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: productSearchCalcActive ? "#fd683e" : "#999",
                },
              },
            }}
          />
        </div>

        {/* Tabs */}
        <div style={{ flexShrink: 0 }}>
          <Tabs
            value={isSearchMode ? false : tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            indicatorColor="primary"
            textColor="primary"
            sx={{
              "& .MuiTab-root": {
                outline: "none",
                whiteSpace: "nowrap",
                minWidth: "auto",
                padding: "6px 16px",
              },
              margin: "0px 5px",
            }}
          >
            {/* Raccourcis */}
            {raccourciGrids.map((raccourci, index) => (
              <Tab
                key={`rac-${raccourci.name}-${index}`}
                label={raccourci.name}
                icon={
                  <span style={{ marginRight: "4px" }}>
                    <i className="fa fa-star" style={{ fontSize: "12px" }}></i>
                  </span>
                }
                iconPosition="start"
              />
            ))}

            {/* Categories */}
            {categories &&
              categories.map(
                (categorie, index) =>
                  categorie.menus_list?.length > 0 && (
                    <Tab
                      key={`cat-${categorie._id}-${index}`}
                      label={categorie.libelle.fr}
                    />
                  ),
              )}
          </Tabs>
        </div>

        {/* Products */}
        <Box
          mt={2}
          sx={{
            margin: 0,
            flex: 1,
            padding: "5px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <section className="content p-2">
            <div className="row gx-2 gy-2">
              {/* Products grid */}
              {getCurrentProducts().length === 0 ? (
                <div className="col-12 text-center" style={{ padding: "40px" }}>
                  <div style={{ color: "#6c757d", fontSize: "16px" }}>
                    <i
                      className="fa fa-box-open fa-2x mb-3"
                      style={{ opacity: 0.5 }}
                    ></i>
                    <p>
                      {isSearchMode
                        ? t("No products found")
                        : t("No products available")}
                    </p>
                  </div>
                </div>
              ) : (
                getCurrentProducts().map((menu, index) => (
                  <div
                    key={menu._id || index}
                    className="col-6 col-sm-4 col-md-3 col-lg-2"
                    style={{ padding: "5px 5px" }}
                  >
                    <Box
                      sx={{
                        cursor: "pointer",
                        borderRadius: 2,
                        overflow: "hidden",
                        boxShadow: 1,
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 4,
                        },
                      }}
                      onClick={() => handleAddToCart(menu)}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          height: 60,
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#f0f0f0",
                        }}
                      >
                        <img
                          src={getImageUrl(menu.image)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "images/product/no-image.png";
                          }}
                          alt={menu.titre?.fr || menu.libelle}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                        {menu.stock != null && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor:
                                menu.stock.quantite_stock <= 0
                                  ? "#d32f2f"
                                  : menu.stock.quantite_stock <= 5
                                    ? "#ed6c02"
                                    : "#2e7d32",
                              color: "#fff",
                              borderRadius: "10px",
                              padding: "1px 6px",
                              fontSize: "0.65rem",
                              fontWeight: "bold",
                              lineHeight: 1.5,
                              pointerEvents: "none",
                            }}
                          >
                            {menu.stock.quantite_stock+ menu.unite}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ textAlign: "center", p: 1 }}>
                        <p
                          className="m-0"
                          style={{
                            fontSize: "0.9rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {menu.titre?.fr || menu.libelle}
                        </p>
                        {isSearchMode && menu.categoryName && (
                          <small
                            style={{ color: "#6c757d", fontSize: "0.7rem" }}
                          >
                            {menu.categoryName}
                          </small>
                        )}
                      </Box>
                    </Box>
                  </div>
                ))
              )}
            </div>
          </section>
        </Box>
      </div>

      <IngredientPopup
        open={ingredientPopupOpen}
        product={currentProduct}
        ingredients={currentProductIngredients}
        onClose={() => setIngredientPopupOpen(false)}
        onConfirm={(selectedIngredients) => {
          onAddToCart({
            id: currentProduct._id || currentProduct.id,
            name: currentProduct.titre?.fr,
            price: currentProduct.prix_ttc,
            ingredients: selectedIngredients,
            productInfo: currentProduct,
          });
          setIngredientPopupOpen(false);
        }}
      />

      <QuickAddProductModal
        open={quickAddOpen}
        barcode={pendingBarcode}
        categories={categories || []}
        organisationId={organisationId}
        onClose={() => setQuickAddOpen(false)}
        onCreated={handleProductCreated}
      />
    </div>
  );
};

export default Product;
